import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Badge,
  ListGroup,
  Modal,
} from 'react-bootstrap';
import SearchableSelect from '../../components/SearchableSelect';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import Loading from '../../components/Loading';

function parseAssets(notes) {
  if (!notes || typeof notes !== 'string') return [];
  const s = notes.indexOf('[[INVENTORY]]');
  const e = notes.indexOf('[[/INVENTORY]]');
  if (s === -1 || e === -1 || e <= s) return [];
  try {
    const jsonText = notes.substring(s + '[[INVENTORY]]'.length, e).trim();
    const data = JSON.parse(jsonText);
    return Array.isArray(data) ? data : [];
  } catch (_) {
    return [];
  }
}

function serializeAssets(notes, assets) {
  const block = `[[INVENTORY]]${JSON.stringify(assets)}[[/INVENTORY]]`;
  if (!notes || typeof notes !== 'string') return block;
  const s = notes.indexOf('[[INVENTORY]]');
  const e = notes.indexOf('[[/INVENTORY]]');
  if (s === -1 || e === -1) return `${notes}\n${block}`;
  return `${notes.substring(0, s)}${block}${notes.substring(e + '[[/INVENTORY]]'.length)}`;
}

export default function CustomerAssets() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [assets, setAssets] = useState([]);
  const [category, setCategory] = useState('telefone');
  const [form, setForm] = useState({});
  const [dynamicKey, setDynamicKey] = useState('');
  const [dynamicValue, setDynamicValue] = useState('');
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editDynamicKey, setEditDynamicKey] = useState('');
  const [editDynamicValue, setEditDynamicValue] = useState('');
  const [assetSearchText, setAssetSearchText] = useState('');
  const [debouncedAssetSearchText, setDebouncedAssetSearchText] = useState('');
  const [catalogManufacturers, setCatalogManufacturers] = useState([]);
  const [catalogModelsByCat, setCatalogModelsByCat] = useState({
    telefone: [],
    servidor: [],
    gateway: [],
    outros: [],
  });
  const [catalogOS, setCatalogOS] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCategory, setEditCategory] = useState('telefone');
  const [catalogCreateErr, setCatalogCreateErr] = useState({});

  const loadCustomer = useCallback(async () => {
    setIsLoading(true);
    try {
      const [custRes, assetsRes] = await Promise.allSettled([
        axios.get(`/customers/${id}`),
        axios.get(`/assets/customer/${id}`),
      ]);
      if (custRes.status === 'fulfilled') {
        setCustomer(custRes.value.data);
      } else {
        setCustomer(null);
      }
      if (assetsRes.status === 'fulfilled') {
        const assetsData = assetsRes.value.data;
        setAssets(Array.isArray(assetsData) ? assetsData : []);
      } else {
        setAssets([]);
      }
    } catch (err) {
      setCustomer(null);
      setAssets([]);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedAssetSearchText(assetSearchText.trim()),
      300
    );
    return () => clearTimeout(t);
  }, [assetSearchText]);

  const loadCatalog = useCallback(async (type, cat) => {
    try {
      const params = { type };
      if (type === 'model' && cat) params.category = cat;
      const { data } = await axios.get('/inventory/catalog', { params });
      const names = (data || []).map((i) => i.name);
      if (type === 'manufacturer') setCatalogManufacturers(names);
      if (type === 'model' && cat)
        setCatalogModelsByCat((prev) => ({ ...prev, [cat]: names }));
      if (type === 'os') setCatalogOS(names);
    } catch (err) {
      if (type === 'manufacturer') setCatalogManufacturers([]);
      else if (type === 'model' && cat)
        setCatalogModelsByCat((prev) => ({ ...prev, [cat]: [] }));
      else if (type === 'os') setCatalogOS([]);
    }
  }, []);
  useEffect(() => {
    loadCatalog('manufacturer');
    loadCatalog('os');
  }, [loadCatalog]);

  useEffect(() => {
    loadCatalog('model', category);
  }, [category, loadCatalog]);

  useEffect(() => {
    if (showEditModal) {
      loadCatalog('model', editCategory);
    }
  }, [showEditModal, editCategory, loadCatalog]);

  const fieldsByCategory = {
    telefone: [
      { key: 'FABRICANTE', label: 'Fabricante' },
      { key: 'MODELO', label: 'Modelo' },
      { key: 'NUMERO_SERIE', label: 'Número de Série' },
      { key: 'MAC', label: 'MAC' },
      { key: 'IP', label: 'IP' },
      {
        key: 'DATA_AQUISICAO',
        label: 'Data de Aquisição/Aluguel',
        type: 'date',
      },
      { key: 'UNIDADE', label: 'Localidade (Unidade)' },
    ],
    servidor: [
      { key: 'HOSTNAME', label: 'Hostname' },
      { key: 'MODELO', label: 'Modelo' },
      { key: 'ESPECIFICACOES', label: 'Especificações' },
      { key: 'SO', label: 'Sistema Operacional' },
      { key: 'IP', label: 'IP' },
      {
        key: 'DATA_AQUISICAO',
        label: 'Data de Aquisição/Aluguel',
        type: 'date',
      },
      { key: 'UNIDADE', label: 'Local' },
    ],
    gateway: [
      { key: 'TIPO', label: 'Tipo (Gateway/ATA/Chipeira)' },
      { key: 'FABRICANTE', label: 'Fabricante' },
      { key: 'MODELO', label: 'Modelo' },
      { key: 'NUMERO_SERIE', label: 'Número de Série' },
      { key: 'IP', label: 'IP' },
      {
        key: 'DATA_AQUISICAO',
        label: 'Data de Aquisição/Aluguel',
        type: 'date',
      },
      { key: 'UNIDADE', label: 'Localidade (Unidade)' },
    ],
    infra: [
      { key: 'FABRICANTE', label: 'Fabricante' },
      { key: 'MODELO', label: 'Modelo' },
      { key: 'NUMERO_SERIE', label: 'Número de Série' },
      {
        key: 'DATA_AQUISICAO',
        label: 'Data de Aquisição/Aluguel',
        type: 'date',
      },
      { key: 'UNIDADE', label: 'Local' },
    ],
  };

  const handleAdd = async () => {
    const payload = {
      customerId: id,
      category,
      fabricante: form.FABRICANTE || form.fabricante || '',
      modelo: form.MODELO || form.modelo || '',
      numeroSerie: form.NUMERO_SERIE || form.numeroSerie || '',
      mac: form.MAC || form.mac || '',
      ip: form.IP || form.ip || '',
      dataAquisicao: form.DATA_AQUISICAO || form.dataAquisicao || '',
      unidade: form.UNIDADE || form.unidade || '',
      hostname: form.HOSTNAME || form.hostname || '',
      so: form.SO || form.so || '',
      especificacoes: form.ESPECIFICACOES || form.especificacoes || '',
    };
    await axios.post('/assets', payload);
    const { data: assetsData } = await axios.get(`/assets/customer/${id}`);
    setAssets(Array.isArray(assetsData) ? assetsData : []);
    loadCatalog('manufacturer');
    loadCatalog('model', category);
    loadCatalog('os');
    setForm({});
    setDynamicKey('');
    setDynamicValue('');
  };

  const handleRemove = async (assetId) => {
    await axios.delete(`/assets/${assetId}`);
    const { data: assetsData } = await axios.get(`/assets/customer/${id}`);
    setAssets(Array.isArray(assetsData) ? assetsData : []);
  };

  const handleEditStart = (a) => {
    setEditingAssetId(a.id);
    const mapped = {
      FABRICANTE: a.fabricante || '',
      MODELO: a.modelo || '',
      NUMERO_SERIE: a.numeroSerie || '',
      MAC: a.mac || '',
      IP: a.ip || '',
      DATA_AQUISICAO: a.dataAquisicao || '',
      UNIDADE: a.unidade || '',
      HOSTNAME: a.hostname || '',
      SO: a.so || '',
      ESPECIFICACOES: a.especificacoes || '',
    };
    setEditForm(mapped);
    setEditCategory(a.category);
    setShowEditModal(true);
    setEditDynamicKey('');
    setEditDynamicValue('');
  };

  const handleEditSave = async () => {
    const payload = {
      category: editCategory,
      fabricante: editForm.FABRICANTE || editForm.fabricante || '',
      modelo: editForm.MODELO || editForm.modelo || '',
      numeroSerie: editForm.NUMERO_SERIE || editForm.numeroSerie || '',
      mac: editForm.MAC || editForm.mac || '',
      ip: editForm.IP || editForm.ip || '',
      dataAquisicao: editForm.DATA_AQUISICAO || editForm.dataAquisicao || '',
      unidade: editForm.UNIDADE || editForm.unidade || '',
      hostname: editForm.HOSTNAME || editForm.hostname || '',
      so: editForm.SO || editForm.so || '',
      especificacoes: editForm.ESPECIFICACOES || editForm.especificacoes || '',
    };
    await axios.put(`/assets/${editingAssetId}`, payload);
    const { data: assetsData } = await axios.get(`/assets/customer/${id}`);
    setAssets(Array.isArray(assetsData) ? assetsData : []);
    loadCatalog('manufacturer');
    loadCatalog('model', editCategory);
    loadCatalog('os');
    setEditingAssetId(null);
    setEditForm({});
    setShowEditModal(false);
  };

  const handleEditCancel = () => {
    setEditingAssetId(null);
    setEditForm({});
  };

  const addDynamicField = () => {
    if (!dynamicKey) return;
    setForm({ ...form, [dynamicKey]: dynamicValue });
    setDynamicKey('');
    setDynamicValue('');
  };

  const removeDynamicField = (k) => {
    const next = { ...form };
    delete next[k];
    setForm(next);
  };

  const addEditDynamicField = () => {
    if (!editDynamicKey) return;
    setEditForm({ ...editForm, [editDynamicKey]: editDynamicValue });
    setEditDynamicKey('');
    setEditDynamicValue('');
  };

  const removeEditDynamicField = (k) => {
    const next = { ...editForm };
    delete next[k];
    setEditForm(next);
  };

  const knownFields = [
    'ip',
    'fabricante',
    'modelo',
    'numeroSerie',
    'hostname',
    'so',
    'especificacoes',
    'dataAquisicao',
    'unidade',
    'mac',
    'empresa_id',
    'customer_id',
    'manufacturer_id',
    'model_id',
    'os_id',
    'createdAt',
    'updatedAt',
  ];

  const filteredAssets = assets.filter((a) => {
    if (!debouncedAssetSearchText) return true;
    const hay = Object.entries(a)
      .filter(([k]) => k !== 'id')
      .map(([, v]) => String(v).toLowerCase())
      .join(' ');
    return hay.includes(debouncedAssetSearchText.toLowerCase());
  });

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title="Inventário"
        pages={[{ name: 'Inventário', link: '/inventory' }]}
      />

      <Card className="mb-3">
        <Card.Header>
          <strong>
            {customer
              ? customer.nomeEmpresa || customer.razaoSocial || 'Cliente'
              : 'Cliente'}
          </strong>
          <Badge bg="secondary" className="ms-2">
            {assets.length} ativos
          </Badge>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Categoria</Form.Label>
                <Form.Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="telefone">Telefone</option>
                  <option value="servidor">Servidor</option>
                  <option value="gateway">Gateway/ATA/Chipeira</option>
                  <option value="infra">
                    Equipamentos de Infra/Periféricos
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            {fieldsByCategory[category].map((f) => {
              const isCatalogField = ['FABRICANTE', 'MODELO', 'SO'].includes(
                f.key
              );
              let optSource = [];
              let typeKey = 'os';
              if (f.key === 'FABRICANTE') {
                optSource = catalogManufacturers;
                typeKey = 'manufacturer';
              } else if (f.key === 'MODELO') {
                optSource = catalogModelsByCat[category] || [];
                typeKey = 'model';
              } else if (f.key === 'SO') {
                optSource = catalogOS;
                typeKey = 'os';
              }
              return (
                <Col md={3} key={f.key}>
                  <Form.Group>
                    <Form.Label>{f.label}</Form.Label>
                    {isCatalogField ? (
                      <>
                        <SearchableSelect
                          options={optSource.map((name) => ({ name }))}
                          value={form[f.key] || ''}
                          valueKey="name"
                          displayKey="name"
                          onChange={(val) =>
                            setForm((prev) => ({ ...prev, [f.key]: val }))
                          }
                          onInputChange={(val) =>
                            setForm((prev) => ({ ...prev, [f.key]: val }))
                          }
                          allowCreate
                          onCreateOption={async (newVal) => {
                            setCatalogCreateErr((s) => ({
                              ...s,
                              [f.key]: null,
                            }));
                            try {
                              await axios.post('/inventory/catalog', {
                                type: typeKey,
                                name: newVal,
                                category:
                                  typeKey === 'model' || typeKey === 'os'
                                    ? category
                                    : undefined,
                              });
                              loadCatalog(typeKey, category);
                            } catch (err) {
                              const msg =
                                err?.response?.data?.error ||
                                err?.response?.data?.message ||
                                err?.message ||
                                'Erro ao criar';
                              setCatalogCreateErr((s) => ({
                                ...s,
                                [f.key]: msg,
                              }));
                              setTimeout(
                                () =>
                                  setCatalogCreateErr((s) => ({
                                    ...s,
                                    [f.key]: null,
                                  })),
                                5000
                              );
                            }
                          }}
                          placeholder={`Digite para selecionar ${f.label.toLowerCase()}...`}
                        />
                        {catalogCreateErr[f.key] && (
                          <small className="text-danger">
                            {catalogCreateErr[f.key]}
                          </small>
                        )}
                      </>
                    ) : (
                      <Form.Control
                        type={f.type || 'text'}
                        value={form[f.key] || ''}
                        onChange={(e) =>
                          setForm({ ...form, [f.key]: e.target.value })
                        }
                      />
                    )}
                  </Form.Group>
                </Col>
              );
            })}
            {category === 'outros' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Campo</Form.Label>
                    <Form.Control
                      type="text"
                      value={dynamicKey}
                      onChange={(e) => setDynamicKey(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Valor</Form.Label>
                    <Form.Control
                      type="text"
                      value={dynamicValue}
                      onChange={(e) => setDynamicValue(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button variant="outline-secondary" onClick={addDynamicField}>
                    Adicionar Campo
                  </Button>
                </Col>
                <Col md={12} className="mt-2">
                  <ListGroup>
                    {Object.entries(form).map(([k, v]) => (
                      <ListGroup.Item
                        key={k}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <Badge bg="secondary" className="me-2">
                            {k}
                          </Badge>
                          {String(v)}
                        </span>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeDynamicField(k)}
                        >
                          Remover
                        </Button>
                      </ListGroup.Item>
                    ))}
                    {Object.keys(form).length === 0 && (
                      <ListGroup.Item className="text-muted">
                        Nenhum campo adicionado
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Col>
              </>
            )}
          </Row>
          <Button onClick={handleAdd}>Adicionar Ativo</Button>
        </Card.Body>
      </Card>
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Ativo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Categoria</Form.Label>
                <Form.Select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                >
                  <option value="telefone">Telefone</option>
                  <option value="servidor">Servidor</option>
                  <option value="gateway">Gateway/ATA/Chipeira</option>
                  <option value="infra">
                    Equipamentos de Infra/Periféricos
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            {fieldsByCategory[editCategory].map((f) => {
              const isCatalogField = ['FABRICANTE', 'MODELO', 'SO'].includes(
                f.key
              );
              let optSource = [];
              if (f.key === 'FABRICANTE') optSource = catalogManufacturers;
              else if (f.key === 'MODELO')
                optSource = catalogModelsByCat[editCategory] || [];
              else if (f.key === 'SO') optSource = catalogOS;
              return (
                <Col md={6} key={f.key} className="mb-2">
                  <Form.Group>
                    <Form.Label>{f.label}</Form.Label>
                    {isCatalogField ? (
                      <>
                        <SearchableSelect
                          options={optSource.map((name) => ({ name }))}
                          value={editForm[f.key] || ''}
                          valueKey="name"
                          displayKey="name"
                          onChange={(val) =>
                            setEditForm({ ...editForm, [f.key]: val })
                          }
                          onInputChange={(val) =>
                            setEditForm({ ...editForm, [f.key]: val })
                          }
                          allowCreate
                          onCreateOption={async (newVal) => {
                            setCatalogCreateErr((s) => ({
                              ...s,
                              [f.key]: null,
                            }));
                            let tKey = 'os';
                            if (f.key === 'FABRICANTE') tKey = 'manufacturer';
                            else if (f.key === 'MODELO') tKey = 'model';
                            try {
                              await axios.post('/inventory/catalog', {
                                type: tKey,
                                name: newVal,
                                category:
                                  tKey === 'model' || tKey === 'os'
                                    ? editCategory
                                    : undefined,
                              });
                              loadCatalog(tKey, editCategory);
                            } catch (err) {
                              const msg =
                                err?.response?.data?.error ||
                                err?.response?.data?.message ||
                                err?.message ||
                                'Erro ao criar';
                              setCatalogCreateErr((s) => ({
                                ...s,
                                [f.key]: msg,
                              }));
                              setTimeout(
                                () =>
                                  setCatalogCreateErr((s) => ({
                                    ...s,
                                    [f.key]: null,
                                  })),
                                5000
                              );
                            }
                          }}
                          placeholder={`Digite para selecionar ${f.label.toLowerCase()}...`}
                        />
                        {catalogCreateErr[f.key] && (
                          <small className="text-danger">
                            {catalogCreateErr[f.key]}
                          </small>
                        )}
                      </>
                    ) : (
                      <Form.Control
                        type={f.type || 'text'}
                        value={editForm[f.key] || ''}
                        onChange={(e) =>
                          setEditForm({ ...editForm, [f.key]: e.target.value })
                        }
                      />
                    )}
                  </Form.Group>
                </Col>
              );
            })}
          </Row>
          {fieldsByCategory[editCategory].length === 0 && (
            <>
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Campo</Form.Label>
                    <Form.Control
                      type="text"
                      value={editDynamicKey}
                      onChange={(e) => setEditDynamicKey(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Valor</Form.Label>
                    <Form.Control
                      type="text"
                      value={editDynamicValue}
                      onChange={(e) => setEditDynamicValue(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={addEditDynamicField}
              >
                Adicionar Campo
              </Button>
              <ListGroup className="mt-2">
                {Object.entries(editForm)
                  .filter(([k]) => k !== 'id' && k !== 'category')
                  .map(([k, v]) => (
                    <ListGroup.Item
                      key={k}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <Badge bg="secondary" className="me-2">
                          {k}
                        </Badge>
                        {String(v)}
                      </span>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeEditDynamicField(k)}
                      >
                        Remover
                      </Button>
                    </ListGroup.Item>
                  ))}
                {Object.keys(editForm).length === 0 && (
                  <ListGroup.Item className="text-muted">
                    Nenhum campo
                  </ListGroup.Item>
                )}
              </ListGroup>
            </>
          )}
          <datalist id="manufacturerOptionsEdit">
            {catalogManufacturers.map((m) => (
              <option key={m} value={m} aria-label={m}>
                {m}
              </option>
            ))}
          </datalist>
          <datalist id="modelOptionsEdit">
            {(catalogModelsByCat[editCategory] || []).map((m) => (
              <option key={m} value={m} aria-label={m}>
                {m}
              </option>
            ))}
          </datalist>
          <datalist id="osOptionsEdit">
            {catalogOS.map((m) => (
              <option key={m} value={m} aria-label={m}>
                {m}
              </option>
            ))}
          </datalist>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => {
              handleEditSave();
            }}
          >
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>

      <Card>
        <Card.Header>Ativos do Cliente</Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6} lg={5} xl={4}>
              <Form.Group controlId="assetSearch">
                <Form.Label>Pesquisar ativo</Form.Label>
                <Form.Control
                  type="search"
                  placeholder="Buscar por qualquer campo do ativo..."
                  value={assetSearchText}
                  onChange={(e) => setAssetSearchText(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="table-responsive">
            <table className="table table-striped table-bordered table-sm align-middle">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>IP</th>
                  <th>Fabricante</th>
                  <th>Modelo</th>
                  <th>Nº Série</th>
                  <th>Hostname</th>
                  <th>SO</th>
                  <th>Especificações</th>
                  <th>Data Aquisição</th>
                  <th>Unidade</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center text-muted py-3">
                      Nenhum ativo encontrado
                    </td>
                  </tr>
                ) : (
                  filteredAssets.map((a) => {
                    const otherPairs = Object.entries(a)
                      .filter(
                        ([k]) => !['id', 'category', ...knownFields].includes(k)
                      )
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(' • ');
                    return (
                      <tr key={a.id}>
                        <td>
                          <Badge bg="info">{a.category}</Badge>
                        </td>
                        <td>
                          <span>{a.ip || '-'}</span>
                        </td>
                        <td>
                          <span>{a.fabricante || '-'}</span>
                        </td>
                        <td>
                          <span>{a.modelo || '-'}</span>
                        </td>
                        <td>
                          <span>{a.numeroSerie || '-'}</span>
                        </td>
                        <td>
                          <span>{a.hostname || '-'}</span>
                        </td>
                        <td>
                          <span>{a.so || '-'}</span>
                        </td>
                        <td>
                          <span>{a.especificacoes || '-'}</span>
                        </td>
                        <td>
                          <span>{a.dataAquisicao || '-'}</span>
                        </td>
                        <td>
                          <span>{a.unidade || '-'}</span>
                        </td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleEditStart(a)}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemove(a.id)}
                            >
                              Remover
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {editingAssetId !== null &&
            fieldsByCategory[
              (
                assets.find((x) => x.id === editingAssetId) || {
                  category: 'infra',
                }
              ).category
            ].length === 0 && (
              <div className="mt-3">
                <Row className="mb-2">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Campo</Form.Label>
                      <Form.Control
                        type="text"
                        value={editDynamicKey}
                        onChange={(e) => setEditDynamicKey(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Valor</Form.Label>
                      <Form.Control
                        type="text"
                        value={editDynamicValue}
                        onChange={(e) => setEditDynamicValue(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="outline-secondary"
                      onClick={addEditDynamicField}
                    >
                      Adicionar Campo
                    </Button>
                  </Col>
                </Row>
                <ListGroup>
                  {Object.entries(editForm)
                    .filter(([k]) => k !== 'id' && k !== 'category')
                    .map(([k, v]) => (
                      <ListGroup.Item
                        key={k}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <Badge bg="secondary" className="me-2">
                            {k}
                          </Badge>
                          {String(v)}
                        </span>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeEditDynamicField(k)}
                        >
                          Remover
                        </Button>
                      </ListGroup.Item>
                    ))}
                  {Object.keys(editForm).length === 0 && (
                    <ListGroup.Item className="text-muted">
                      Nenhum campo
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </div>
            )}
        </Card.Body>
      </Card>
    </Container>
  );
}
